import React, { useState, useEffect, useCallback } from 'react';
import type { GetItemsFilters, MasterDataForComponents } from '../../../types';
import { useItemFilters } from '../../../contexts/ItemFilterContext';
import { formatFromDateInput } from '../../../utils/date';
import { CaltivationCategoryOptions, ProductionHistoryOptions } from '../../../constants';
import { Search } from 'lucide-react';
import { MasterSearchDialog } from '../../common/MasterSearch/MasterSearchDialog';
import { useCodeName } from '../../../hooks/useUserMasterCodeName';
import { MasterCategoryConst, type MasterCategoryType } from '../../../types/masterSearchTypes';

// # Propsの型定義

// 検索ダイアログ用Props
interface BiddingInputFilterDialogProps {
  isOpen: boolean;
  onClose: () => void;
  masterData: MasterDataForComponents
  isSeller: boolean;
  isFarmer: boolean;
}

// ソート可能な項目の定義
const sortOptions = [
  { value: '', label: '指定なし' },
  { value: 'BiddingNumber', label: '入札No.' },
  { value: 'SellerCode', label: '売手' },
  { value: 'FarmerCode', label: '生産者' },
  { value: 'Brand', label: '荷印' },
  { value: 'TeaSeasonCode', label: '茶期' },
  { value: 'TeaTypeCode', label: '茶種' },
  { value: 'VarietyCode', label: '品種名' },
  { value: 'GapCode', label: 'GAP区分' },
  { value: 'CaltivationCategory', label: '栽培区分' },
  { value: 'ProductionHistory', label: '生産履歴' },
  { value: 'Status', label: '出品状況'},
];

// # 検索条件指定ダイアログコンポーネント
export const BiddingInputFilterDialog: React.FC<BiddingInputFilterDialogProps> = ({ isOpen, onClose, masterData, isSeller, isFarmer }) => {

  // # Context使用定義

  // フィルターcontextより検索条件を取得
  const { itemFilters, updateFiltersAndNavigate } = useItemFilters();

  // # State定義

  // モーダル内のローカルstateを定義する
  const [localFilters, setLocalFilters] = useState<GetItemsFilters>(itemFilters);

  // カスタムフック：名称表示用の codes/names 管理
  const { codes, setCodes, names } = useCodeName();

  // 検索ダイアログ制御用の状態
  const [isSearchOpen, setIsSearchOpen] = useState(false); // ダイアログの開閉
  const [searchCategory, setSearchCategory] = useState<MasterCategoryType>(MasterCategoryConst.SELLER); // 検索対象マスタ
  const [activeTarget, setActiveTarget] = useState<'seller' | 'farmer'>('seller'); // どの入力欄に対する検索か  

  // # Effect定義
  // フィルターが開くたびに、ローカルstateをフィルターContextの最新状態(itemFilters)の内容でリセット
  useEffect(() => {
    if (isOpen) {
      // 1. Contextの検索条件をコピー
      const initialFilters = { ...itemFilters };
      // '' (すべて) を初期値
      if (initialFilters.gapCode === undefined) {
      initialFilters.gapCode = '';
      }
      
      // 3. ローカルのStateにセットする 
      setLocalFilters(initialFilters);

      // 名称表示用フックにコードを同期
      setCodes({
        seller: initialFilters.sellerCode || null,
        farmer: initialFilters.farmerCode || null,
        buyer: null,
      });
    }
  }, [isOpen, itemFilters, setCodes]);

  // # イベントハンドラー定義

  // フォーム入力ハンドラ
  // ローカルstateを入力内容で更新する
  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;

    // 1.入力内容を適切な内容に変換
    let processedValue: string | number = value;
    let sanitizedValue = value;

    // 1-a.日付は yyyyMMdd に変換
    if (type === 'date') {
      processedValue = formatFromDateInput(value);
    } else {
      // 1-b.数値（''を許容）
      const isNumeric = ['sellerCode', 'farmerCode', 'teaSeasonCode', 'teaTypeCode', 'varietyCode', 'gapCode', 'caltivationCategory', 'productionHistory', 'listingCategory', 'status'].includes(name);
      if (isNumeric) {
        sanitizedValue = value.replace(/[^0-9]/g, '');
        processedValue = (sanitizedValue === '' ? '' : Number(sanitizedValue));
      } else {
        // 1-c.文字列
        processedValue = value;
      }
    }

    // ローカルstateを入力内容で更新する
    setLocalFilters(prev => {
      // 1. まず新しい値で更新
      const newFilters = { ...prev, [name]: processedValue };

      // 2. 売り手が変わった際の連動処理
      if (name === 'sellerCode') {
        const numVal = sanitizedValue === '' ? null : Number(sanitizedValue);
        setCodes(p => ({ ...p, seller: numVal, farmer: null, buyer: null })); // フック側同期
        newFilters.farmerCode = ''; 
        newFilters.gapCode = ''; // 売手変更時にGAPを空(すべて)に戻す
      }

      // 3. 生産者が変わった時のGAP連動処理
      if (name === 'farmerCode') {
        const numVal = sanitizedValue === '' ? null : Number(sanitizedValue);
        setCodes(p => ({ ...p, farmer: numVal, buyer: null })); // フック側同期
        if (processedValue === '') {
          // 生産者が未選択に戻されたらGAP区分を空にする
          newFilters.gapCode = '';
        } else {
          // 売手と生産者の複合キーでマスタから検索
          const selectedFarmer = masterData.farmers.find(
            (f) =>
              String(f.sellerCode) === String(newFilters.sellerCode) &&
              String(f.farmerCode) === String(processedValue)
          );
          if (selectedFarmer && selectedFarmer.gapCode != null) {
            newFilters.gapCode = selectedFarmer.gapCode;
          } else {
            newFilters.gapCode = ''; // マスタに無い場合も空
          }
        }
      }

      // 4. 最終的な新しい状態を返す
      return newFilters;
    });
  }, [masterData.farmers, setCodes]); // setLocalFilters内でマスタを参照するため依存配列に追加

  // ------------------------------------------------
  // ソートキー変更ハンドラ
  // ------------------------------------------------
  const handleSortChange = useCallback((index: number, value: string) => {
    setLocalFilters(prev => {
      // 現在の sortKeys をコピー (なければ空配列)
      const currentSorts = [...(prev.sortKeys || [])];
      
      // 指定インデックスを更新
      if (value) {
        // 値があればセット (配列の長さを超える場合は埋める)
        currentSorts[index] = value;
      } else {
        // "指定なし" が選ばれたら、そのインデックス以降を詰める（削除する）
        currentSorts.splice(index, 1);
      }

      return { ...prev, sortKeys: currentSorts };
    });
  }, []);

  // 現在のソートキーを取得するヘルパー (index番目の値、なければ空文字)
  const getSortKey = (index: number) => {
    return localFilters.sortKeys?.[index] || '';
  };

  // 一括クリアボタンハンドラ
  const handleClear = useCallback(() => {
    // フック側のコードをクリア
    setCodes({ 
      seller: (isSeller || isFarmer) ? localFilters.sellerCode || null : null, 
      farmer: isFarmer ? localFilters.farmerCode || null : null,
      buyer: null
    });

    setLocalFilters(prev => ({
      ...prev,          // ページング情報など、他のパラメータがあれば維持
      sellerCode: (isSeller || isFarmer) ? prev.sellerCode : '',   
      farmerCode: isFarmer ? prev.farmerCode : '',
      brand: '',
      teaSeasonCode: '',
      teaTypeCode: '',
      varietyCode: '',
      gapCode: '',       
      caltivationCategory: '',
      productionHistory: '',
      status:'',
      sortKeys: []    
    }));
  }, [isSeller, isFarmer, localFilters.sellerCode, localFilters.farmerCode, setCodes]);

  // 適用ボタンハンドラ
  const handleApply = useCallback(() => {
    // 1. ローカルstateをもとにフィルターcontext経由でURLクエリパラメータを更新する
    updateFiltersAndNavigate(localFilters);
    // 2. モーダルを閉じる（ローカルstateは破棄される）
    onClose();
  }, [localFilters, updateFiltersAndNavigate, onClose]);

  // キャンセルボタンハンドラ
  const handleCancel = useCallback(() => {
    onClose();
  }, [onClose]);

  /** マスタ検索ダイアログを表示する */
  const openSearch = (cat: MasterCategoryType, target: 'seller' | 'farmer') => {
    if (target === 'farmer' && !localFilters.sellerCode) return;
    setSearchCategory(cat);
    setActiveTarget(target);
    setIsSearchOpen(true);
  };

  /** ダイアログでマスタを選択した際の処理 */
  const handleSelectMaster = (selectedData: any) => {
    if (activeTarget === 'seller') {
      const code = selectedData.sellerCode ?? null;
      setCodes(p => ({ ...p, seller: code, farmer: null, buyer: null }));
      setLocalFilters(p => ({ 
        ...p, 
        sellerCode: code ?? "", 
        farmerCode: "", 
        gapCode: '' 
      }));
      setTimeout(() => {
        const nextEl = document.getElementsByName('farmerCode')[0] as HTMLElement;
        nextEl?.focus();
      }, 100);
    } else if (activeTarget === 'farmer') {
      const fCode = selectedData.farmerCode ?? null;
      setCodes(p => ({ ...p, farmer: fCode, buyer: null }));
      setLocalFilters(p => ({ 
        ...p, 
        farmerCode: fCode ?? "", 
        gapCode: selectedData.gapCode ?? ''
      }));
      setTimeout(() => {
        const nextEl = document.getElementsByName('brand')[0] as HTMLElement;
        nextEl?.focus();
      }, 100);        
    }
    setIsSearchOpen(false);
  };

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLElement>) => {
    if (e.key === "Enter") {
      // 備考欄などでEnterを押したときにフォーム送信されるのを防ぐ
      e.preventDefault();

      const container = e.currentTarget.closest("div.bg-white") || document;
      // フォーカス可能な要素をすべて取得
      const focusableElements = Array.from(
        container.querySelectorAll('input:not([disabled]), select:not([disabled]), button:not([disabled])')
      ) as HTMLElement[];

      let index = focusableElements.indexOf(e.currentTarget);

      if (index > -1) {
        let nextIndex = index + 1;

        while (nextIndex < focusableElements.length) {
          const nextElement = focusableElements[nextIndex] as HTMLInputElement;
          
          const isCheckbox = nextElement.type === 'checkbox';
          const isRadio = nextElement.type === 'radio';
          // 検索ボタン（アイコンボタン）はスキップしたいが、保存ボタン（テキストがあるボタン）は止めたい
          // もしくは「bg-indigo-600」などのクラス名で判定する
          const isSearchButton = nextElement.tagName === 'BUTTON' && !nextElement.innerText.includes("検索条件を適用");
          const isDisabled = (nextElement as any).disabled;

          if (isCheckbox || isRadio || isSearchButton || isDisabled) {
            nextIndex++;
          } else {
            // 保存ボタン、または次の入力項目が見つかったらフォーカス
            nextElement.focus();
            break;
          }
        }
      }
    }
  }, []);

  // # その他定義

  // 入力項目の共通スタイル
  const inputClasses = "mt-1 w-full p-2 border border-slate-300 rounded-md bg-white focus:ring-indigo-500 focus:border-indigo-500 h-[38px]";
  // 表示項目の共通スタイル
  const labelClasses = "block text-sm font-medium text-slate-700";
  // マスタコード入力欄専用スタイル
  const codeInputClasses = "mt-1 w-16 p-2 border border-slate-300 rounded-l-md bg-white focus:ring-1 focus:ring-indigo-500 text-sm h-[38px] outline-none shadow-sm disabled:bg-slate-200 disabled:cursor-not-allowed text-left";
  // 名称表示エリア
  const nameDisplayClasses = "ml-1 mt-1 flex-1 text-sm text-slate-600 truncate font-medium bg-slate-200 border border-slate-300 rounded-md px-2 h-[38px] flex items-center min-w-0";
  // レンダリング処理
  if (!isOpen) return null; // 開いていなければ何もレンダリングしない

  return (
    // モーダルオーバーレイ
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" onClick={handleCancel}>
      {/* モーダルコンテンツ */}
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full flex flex-col max-h-[90vh]" onClick={e => e.stopPropagation()}>
        
        {/* 1. ヘッダーエリア (Sticky) */}
        <div className="p-6 border-b border-slate-200 flex-shrink-0">
          <h2 className="text-xl font-semibold mb-0">検索条件</h2>
        </div>

        {/* 2. コンテンツエリア (Scrollable) */}
        <div className="p-6 overflow-y-auto"> 
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
            
            {/* 売手・生産者：PC(lg)で合計3列分を専有する */}
            <div className="md:col-span-2 lg:col-span-3 flex flex-col md:flex-row gap-4 md:gap-6">
              {/* 売手 */}
              <div className="md:w-1/2 min-w-0">
                <label className={labelClasses}>売手</label>
                <div className="flex items-center gap-0">
                  <input
                    type="text"
                    name="sellerCode"
                    value={codes.seller ?? ''}
                    onKeyDown={handleKeyDown}
                    onChange={handleChange}
                    disabled={isSeller || isFarmer}
                    className={codeInputClasses}
                  />
                  <button 
                    type="button"
                    disabled={isSeller || isSeller || isFarmer}
                    onClick={() => openSearch(MasterCategoryConst.SELLER, 'seller')} 
                    className="mt-1 px-2 bg-slate-100 border border-l-0 border-slate-300 rounded-r-md hover:bg-slate-200 h-[38px] flex items-center justify-center transition-colors disabled:bg-slate-200"
                  >
                    <Search size={16} className="text-slate-500" />
                  </button>
                  <div className={nameDisplayClasses}>
                    {names.seller}
                  </div>
                </div>
              </div>

              {/* 生産者 */}
              <div className="md:w-1/2 min-w-0">
                <label className={labelClasses}>生産者</label>
                <div className="flex items-center gap-0">
                  <input
                    type="text"
                    name="farmerCode"
                    value={codes.farmer ?? ''}
                    onKeyDown={handleKeyDown}
                    onChange={handleChange}
                    disabled={isFarmer || !localFilters.sellerCode}
                    className={codeInputClasses}
                  />
                  <button 
                    type="button"
                    disabled={isFarmer || !localFilters.sellerCode}
                    onClick={() => openSearch(MasterCategoryConst.FARMER, 'farmer')} 
                    className="mt-1 px-2 bg-slate-100 border border-l-0 border-slate-300 rounded-r-md hover:bg-slate-200 h-[38px] flex items-center justify-center transition-colors disabled:bg-slate-200"
                  >
                    <Search size={16} className="text-slate-500" />
                  </button>
                  <div className={nameDisplayClasses}>
                    {names.farmer}
                  </div>
                </div>
              </div>
            </div>

            <div className="form-group lg:col-span-1"><label className={labelClasses}>荷印</label><input type="text" name="brand" value={localFilters.brand} 
              onKeyDown={handleKeyDown}
              onChange={handleChange} className={inputClasses}/></div>
            
            {/* マスタ系 */}
            <div className="form-group lg:col-span-1"><label className={labelClasses}>茶期</label><select name="teaSeasonCode" value={localFilters.teaSeasonCode} 
              onKeyDown={handleKeyDown}
              onChange={handleChange} className={inputClasses}><option value="">すべて</option>{masterData.teaSeasons.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}</select></div>
            <div className="form-group lg:col-span-1"><label className={labelClasses}>茶種</label><select name="teaTypeCode" value={localFilters.teaTypeCode} 
              onKeyDown={handleKeyDown}
              onChange={handleChange} className={inputClasses}><option value="">すべて</option>{masterData.teaTypes.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}</select></div>
            <div className="form-group lg:col-span-1"><label className={labelClasses}>品種名</label><select name="varietyCode" value={localFilters.varietyCode} 
              onKeyDown={handleKeyDown}
              onChange={handleChange} className={inputClasses}><option value="">すべて</option>{masterData.varieties.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}</select></div>
            
            <div className="form-group lg:col-span-1">
              <label className={labelClasses}>GAP区分</label>
              <select name="gapCode" value={localFilters.gapCode ?? ''} 
                onKeyDown={handleKeyDown}
                onChange={handleChange} className={inputClasses}>
                <option value="">すべて</option>
                <option value={9}>GAPなし</option>
                {masterData.gaps
                  .filter((g) => g.gapCode !== 9 && g.gapCode !== 0)
                  .map((v) => (
                    <option key={v.gapCode} value={v.gapCode}>{v.gapName}</option>
                  ))}
              </select>
            </div>

            <div className="form-group lg:col-span-1"><label className={labelClasses}>栽培区分</label><select name="caltivationCategory" value={localFilters.caltivationCategory} 
              onKeyDown={handleKeyDown}
              onChange={handleChange} className={inputClasses}><option value="">すべて</option>{CaltivationCategoryOptions.map(v => <option key={v.value} value={v.value}>{v.label}</option>)}</select></div>
            <div className="form-group lg:col-span-1"><label className={labelClasses}>生産履歴</label><select name="productionHistory" value={localFilters.productionHistory} 
              onKeyDown={handleKeyDown}
              onChange={handleChange} className={inputClasses}><option value="">すべて</option>{ProductionHistoryOptions.map(v => <option key={v.value} value={v.value}>{v.label}</option>)}</select></div>
          </div>

          {/* --- ソート順エリア --- */}
          <div>
            <h3 className="text-base font-semibold text-slate-700 mt-6 mb-4">並び替え順 (最大3つ)</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              
              {/* ソート順 1 */}
              <div className="form-group">
                <label className={labelClasses}>優先順位 1</label>
                <select 
                  value={getSortKey(0)} 
                  onKeyDown={handleKeyDown}
                  onChange={(e) => handleSortChange(0, e.target.value)} 
                  className={inputClasses}
                >
                  {sortOptions.map(opt => (
                    <option key={`sort1-${opt.value}`} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>

              {/* ソート順 2 */}
              <div className="form-group">
                <label className={labelClasses}>優先順位 2</label>
                <select 
                  value={getSortKey(1)} 
                  onKeyDown={handleKeyDown}
                  onChange={(e) => handleSortChange(1, e.target.value)} 
                  className={inputClasses}
                  disabled={!getSortKey(0)} // 1つ目が選ばれてないと無効
                >
                  {sortOptions.map(opt => (
                    <option key={`sort2-${opt.value}`} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>

              {/* ソート順 3 */}
              <div className="form-group">
                <label className={labelClasses}>優先順位 3</label>
                <select 
                  value={getSortKey(2)} 
                  onKeyDown={handleKeyDown}
                  onChange={(e) => handleSortChange(2, e.target.value)} 
                  className={inputClasses}
                  disabled={!getSortKey(1)} // 2つ目が選ばれてないと無効
                  >
                    {sortOptions.map(opt => (
                    <option key={`sort3-${opt.value}`} value={opt.value}>{opt.label}</option>
                    ))}
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* 3. ボタンエリア (Sticky) */}
        <div className="p-4 md:p-6 border-t border-slate-200 sticky bottom-0 bg-white z-10">
          
          {/* フレックスコンテナ：スマホは縦積み(reverse)、PCは横並び */}
          <div className="flex flex-col-reverse md:flex-row justify-between items-center gap-4">
            
            {/* ▼▼▼ 左側 (スマホでは下段): 一括クリアボタン ▼▼▼ */}
            <button 
              onClick={handleClear} 
              className="w-full md:w-auto px-4 py-3 md:py-2 text-sm text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-md transition-colors border border-slate-200 md:border-transparent"
            >
              条件を一括クリア
            </button>

            {/* ▼▼▼ 右側 (スマホでは上段): キャンセル・適用ボタン ▼▼▼ */}
            <div className="grid grid-cols-2 w-full md:w-auto md:flex gap-3 md:gap-4">
              <button 
                onClick={handleCancel} 
                className="w-full md:w-auto px-4 py-3 md:py-2 rounded-md bg-slate-200 hover:bg-slate-300 font-medium"
              >
                キャンセル
              </button>
              
              <button 
                onClick={handleApply} 
                className="w-full md:w-auto px-4 py-3 md:py-2 rounded-md bg-indigo-600 text-white hover:bg-indigo-700 font-semibold shadow-sm"
              >
                検索条件を適用
              </button>
            </div>
            
          </div>
        </div>
      </div>

      {/* 汎用マスタ検索ダイアログ */}
      <MasterSearchDialog 
        isOpen={isSearchOpen} 
        onClose={() => setIsSearchOpen(false)} 
        category={searchCategory} 
        initialValues={{ sellerCode: localFilters.sellerCode, sellerName: names.seller }} 
        onSelect={handleSelectMaster} 
      />
    </div>
  );
};